import { createCadmusEditor } from "@vericus/cadmus-editor-prosemirror";

import { render } from "dom-serializer";
import { AnyNode, ChildNode, DomHandler, Element } from "domhandler";
import {
  findAll,
  findOne,
  getAttributeValue,
  isTag,
  replaceElement,
} from "domutils";
import { Parser } from "htmlparser2";
import { filetypemime } from "magic-bytes.js";
import { MathMLToLaTeX } from "mathml-to-latex";

export interface Base64File {
  filename: string;
  extension?: string;
  base64String: string;
}

export function getBase64Mime(base64String: string): string | undefined {
  return filetypemime(
    Uint8Array.from(atob(base64String), (c) => c.charCodeAt(0))
  )?.[0];
}

/**
 * Patch imported HTML to fit into the editor by doing these steps:
 * - Parse file and inline it as a base64 string
 * - Put width into table's cell
 **/
export async function patchHTML(
  str: string,
  getBase64File: (src: string) => Promise<Base64File | null>
) {
  const dom = new DOMParser().parseFromString(str, "text/html");
  const images = dom?.querySelectorAll("img");
  for await (const img of images) {
    if (!img.src || /^data/.test(img.src)) return;
    const base64File = await getBase64File(img.src);
    if (base64File) {
      const { base64String, extension, filename } = base64File;
      img.src = `data:image/${extension};base64;filename:${filename},${base64String}`;
    }
    const parent = img.parentElement;
    // make sure that image is a block image instead of inline as
    // only block image is supported
    if (parent && parent.tagName.toLowerCase() !== "figure") {
      const figure = document.createElement("figure");
      figure.appendChild(img);
      if (parent.nextSibling && parent.parentNode) {
        parent.parentNode.insertBefore(figure, parent.nextSibling);
      } else if (parent.parentNode) {
        parent.parentNode.appendChild(figure);
      } else {
        dom?.appendChild(figure);
      }
    }
  }
  // Update cell width of tables
  const tables = dom?.querySelectorAll("table");
  tables?.forEach((table) => {
    const trs = table.querySelectorAll("tr");
    trs?.forEach((tr) => {
      const cols = tr.querySelectorAll("td,th");
      if (cols) {
        const colNum =
          Array.from(cols).reduce((acc, col) => {
            const colSpan = col.getAttribute("colSpan") ?? "1";
            return acc + parseInt(colSpan, 10);
          }, 0) ?? 1;
        cols.forEach((col) => {
          const colSpan = col.getAttribute("colSpan") ?? "1";
          const colWidth = (800 / colNum) * parseInt(colSpan, 10);
          col.setAttribute("colwidth", `${colWidth}`);
        });
      }
    });
  });
  return dom?.body?.innerHTML;
}

/**
 * Decode HTML entities in plain text
 *
 * The QTI XML parser runs in xmlMode, which only decodes the five XML entities
 * (&lt; &gt; &amp; &quot; &apos;) and numeric refs; named HTML entities such as
 * &mdash;, &nbsp; or &eacute; survive in the text. This finishes decoding them
 * via the browser's full entity table and returns plain text.
 **/
export function decodeHtmlEntities(str: string): string {
  return (
    new DOMParser().parseFromString(str, "text/html").body.textContent ?? str
  );
}

const mathMLRegex = /^m:(m.*)/gi;

/**
 * Parse mathml elements as latex
 **/
export function patchMath(contents: AnyNode[]) {
  const rootMaths = findAll(
    (e) =>
      mathMLRegex.test(e.tagName) &&
      !!e.parent &&
      isTag(e.parent) &&
      !mathMLRegex.test(e.parent.tagName),
    contents
  );
  rootMaths.forEach((m) => {
    const display = getAttributeValue(m, "display") ?? "inline";
    const mathml = render(m).replace(/((<\/)?m:)(m.*?>)/gi, "$2$3");
    replaceElement(
      m,
      new Element(display === "inline" ? "span" : "div", {
        "data-math-latex": MathMLToLaTeX.convert(mathml),
      })
    );
  });
  return contents;
}

/** Custom domutils helper to recursively go through tagNames from the rootNode */
export function getElementByPath(node: AnyNode[], tagNames: string[]) {
  let result: { currentNodes: ChildNode[]; element: Element | null } = {
    currentNodes: node,
    element: null,
  };
  tagNames.every((tagName, index) => {
    const element = findOne(
      (e) => e.tagName === tagName,
      result.currentNodes,
      false
    );
    if (!element) return false;
    result = {
      currentNodes: element.childNodes,
      element: index === tagNames.length - 1 ? element : null,
    };
    return true;
  });
  return result.element;
}

/**
 * Helper function to parse XML string to XML elements
 **/
export function getParsedXML(xmlString: string) {
  const handler = new DomHandler(undefined, { xmlMode: true });
  const parser = new Parser(handler, { xmlMode: true });
  parser.end(xmlString);
  return handler.root;
}

/**
 * Get text content of a file if it's xml file or return itself
 * if it's a string otherwise return null
 **/
export async function getXMLFileText(file: string | File) {
  let fileText: string;
  if (file instanceof File) {
    if (!file.name.includes(".xml")) return null;
    fileText = await file.text();
  } else {
    fileText = file;
  }
  return fileText;
}

export interface Props {
  id: string;
  promptDoc: string | null;
  enableBlanks?: boolean;
}

/**
 * Helper function to create a cadmus editor for question preview in question
 * library file import feature
 **/
export function createQuestionPreviewEditor(props: Props) {
  return createCadmusEditor({
    editorId: props.id,
    content: props.promptDoc,
    editable: false,
    // Read-only surface, no paste risk: always render saved font marks.
    enableFontControls: true,
    enableBlanks: true,
    enableImage: true,
  });
}
